import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { useSeo } from "../lib/seo";
import { SOCIALS } from "../lib/data";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  { value: "Press", label: "Press" },
  { value: "Business", label: "Business" },
  { value: "Artists", label: "Artists" },
  { value: "Other", label: "Other" },
];

export default function Contact() {
  useSeo({
    title: "Shaji Nada | Contact — Press, Business & Artist Inquiries",
    description:
      "Contact Shaji Nada — for press, business, and artist inquiries. Reach the press desk or business team for Maajja and Ideal Entertainment.",
    canonicalPath: "/contact",
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    inquiry_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.inquiry_type || !form.message) {
      toast.error("Please fill the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      toast.success("Message received. We'll be in touch.");
      setForm({ name: "", email: "", organization: "", inquiry_type: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 md:pt-40 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="overline mb-6">Contact</p>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.98] max-w-5xl" data-testid="contact-h1">
          Let's talk — <span className="italic text-[var(--accent)]">press, partners, artists.</span>
        </h1>
      </section>

      <section className="mt-20 md:mt-28 max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20">
        {/* Form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="md:col-span-7 space-y-8"
          data-testid="contact-form"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="overline" htmlFor="name">Name</Label>
              <Input
                id="name"
                data-testid="contact-name"
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="mt-3 rounded-none bg-transparent border-0 border-b border-white/20 focus-visible:ring-0 focus-visible:border-[var(--accent)] px-0 text-lg h-12 text-white"
              />
            </div>
            <div>
              <Label className="overline" htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="mt-3 rounded-none bg-transparent border-0 border-b border-white/20 focus-visible:ring-0 focus-visible:border-[var(--accent)] px-0 text-lg h-12 text-white"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="overline" htmlFor="organization">Organization</Label>
              <Input
                id="organization"
                data-testid="contact-organization"
                value={form.organization}
                onChange={(e) => update("organization", e.target.value)}
                className="mt-3 rounded-none bg-transparent border-0 border-b border-white/20 focus-visible:ring-0 focus-visible:border-[var(--accent)] px-0 text-lg h-12 text-white"
              />
            </div>
            <div>
              <Label className="overline" htmlFor="inquiry">Inquiry Type</Label>
              <Select value={form.inquiry_type} onValueChange={(v) => update("inquiry_type", v)}>
                <SelectTrigger
                  id="inquiry"
                  data-testid="contact-inquiry"
                  className="mt-3 rounded-none bg-transparent border-0 border-b border-white/20 focus:ring-0 focus:border-[var(--accent)] px-0 text-lg h-12 text-white"
                >
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border border-white/10 rounded-none text-white">
                  {INQUIRY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`inquiry-opt-${t.value.toLowerCase()}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="overline" htmlFor="message">Message</Label>
            <Textarea
              id="message"
              data-testid="contact-message"
              required
              rows={6}
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              className="mt-3 rounded-none bg-transparent border-0 border-b border-white/20 focus-visible:ring-0 focus-visible:border-[var(--accent)] px-0 text-base text-white resize-none"
              placeholder="Tell us a little about your inquiry…"
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="inline-flex items-center gap-3 px-8 py-5 border border-[var(--accent)] text-[var(--accent)] uppercase tracking-[0.22em] text-xs font-medium hover:bg-[var(--accent)] hover:text-black transition-colors duration-300 disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Send message"}
              <ArrowUpRight size={14} />
            </button>
          </div>

          {success && (
            <div className="flex items-center gap-3 text-[var(--accent)] mt-6" data-testid="contact-success">
              <CheckCircle size={20} weight="duotone" />
              <span>Message received. We'll be in touch.</span>
            </div>
          )}
        </motion.form>

        {/* Sidebar */}
        <aside className="md:col-span-5 space-y-12" data-testid="contact-sidebar">
          <div>
            <p className="overline mb-5">Direct</p>
            <ul className="space-y-3 text-lg text-white/80">
              <li>
                Email: <a href="mailto:Shaji@idealgroup.ca" data-testid="contact-direct-email" className="link-underline text-white">Shaji@idealgroup.ca</a>
              </li>
              <li>
                Phone: <a href="tel:+14163566500" data-testid="contact-direct-phone" className="link-underline text-white">+1 (416) 356-6500</a>
              </li>
            </ul>
          </div>

          <div>
            <p className="overline mb-5">Follow</p>
            <ul className="space-y-3 text-lg text-white/80">
              {SOCIALS.map((s) => (
                <li key={s.name}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-social-${s.name.toLowerCase()}`}
                    className="link-underline text-white inline-flex items-center gap-2"
                  >
                    {s.name} <ArrowUpRight size={14} />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-4 border-t border-white/10">
            <p className="overline mb-3">Based</p>
            <p className="text-white/70 font-light">Toronto · Chennai · Worldwide</p>
          </div>
        </aside>
      </section>
    </div>
  );
}
